import useSWR from "swr";
import { fetcher, api } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping, formatPct, formatDate } from "@/lib/format";
import { useState, useMemo } from "react";
import { X, Sparkles, ChevronRight } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid,
} from "recharts";

const CATEGORY_TONE = {
  National: "border-primary text-primary",
  Regional: "border-foreground text-foreground",
  International: "border-muted-foreground text-muted-foreground",
};

function YoYBadge({ value }) {
  if (value === null || value === undefined) return null;
  const pos = value >= 0;
  return (
    <span className={`hairline px-2 h-6 inline-flex items-center font-mono text-[11px] ${
      pos ? "text-positive border-positive/40" : "text-destructive border-destructive/40"
    }`}>
      YoY {formatPct(value, { signed: true })}
    </span>
  );
}

function FestivalCard({ f, onOpen }) {
  return (
    <button
      data-testid="festival-card-trigger"
      onClick={() => onOpen(f)}
      className="text-left hairline p-5 hover:bg-secondary/50 transition-colors group"
    >
      <div className="flex items-start justify-between">
        <span className={`hairline px-2 py-0.5 text-[10px] uppercase tracking-widest ${CATEGORY_TONE[f.category] || ""}`}>
          {f.category}
        </span>
        {f.is_upcoming && (
          <span className="font-mono text-[11px] text-primary">in {f.days_away}d</span>
        )}
      </div>
      <div className="font-display text-xl md:text-2xl mt-3 leading-tight">{f.name}</div>
      <div className="label-caps mt-1">{formatDate(f.start_date)} → {formatDate(f.end_date)}</div>
      <div className="mt-4 flex items-baseline justify-between">
        <div>
          <div className="label-caps">Sales</div>
          <div className="font-mono text-lg mt-1">{formatIndianCurrency(f.net_sales)}</div>
        </div>
        <YoYBadge value={f.yoy_change} />
      </div>
      <div className="mt-5 flex items-center text-xs text-muted-foreground group-hover:text-foreground">
        Open festival → <ChevronRight className="w-3 h-3 ml-0.5" />
      </div>
    </button>
  );
}

/* Treemap with auto-fit SVG text */
function TreemapTile({ name, value, percent, x, y, w, h, color }) {
  // font sizing: rough fit
  const fontSize = Math.max(9, Math.min(h / 4, w / Math.max(name.length * 0.55, 6)));
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={color} stroke="hsl(var(--background))" strokeWidth={1} />
      <foreignObject x={x + 6} y={y + 6} width={w - 12} height={h - 12}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{ color: "white", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ fontSize: `${fontSize}px`, fontFamily: "Fraunces, serif", lineHeight: 1.05, wordBreak: "break-word" }}>{name}</div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: `${Math.max(9, fontSize * 0.7)}px`, opacity: 0.85 }}>
            {percent}%
          </div>
        </div>
      </foreignObject>
    </g>
  );
}

function Treemap({ items }) {
  // Simple slice-and-dice layout
  const W = 800, H = 360;
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  let x = 0;
  const cols = Math.ceil(Math.sqrt(items.length));
  const colWidth = W / cols;
  const rowsPerCol = Math.ceil(items.length / cols);
  const tiles = [];
  for (let c = 0; c < cols; c++) {
    const colItems = items.slice(c * rowsPerCol, (c + 1) * rowsPerCol);
    const colSum = colItems.reduce((s, i) => s + i.value, 0) || 1;
    let y = 0;
    for (const it of colItems) {
      const h = (it.value / colSum) * H;
      tiles.push({ ...it, x, y, w: colWidth, h });
      y += h;
    }
    x += colWidth;
  }
  const PALETTE = ["#1E40FF", "#3B5BFF", "#5A75FF", "#6B7280", "#4B5563", "#374151", "#1F2937", "#0A1628", "#A1A1AA", "#52525B"];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }} className="hairline">
      {tiles.map((t, i) => (
        <TreemapTile key={t.name} {...t} color={PALETTE[i % PALETTE.length]} />
      ))}
    </svg>
  );
}

function FestivalModal({ f, onClose }) {
  const url = `/analysis/festival-detail?festival_name=${encodeURIComponent(f.name)}&start_date=${f.start_date}&end_date=${f.end_date}`;
  const { data: detail } = useSWR(url, fetcher);
  const [tab, setTab] = useState("overview");
  const [recs, setRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const loadRecs = async () => {
    setLoadingRecs(true);
    try {
      const { data } = await api.get(`/ai/festival-recommendations?festival_name=${encodeURIComponent(f.name)}`);
      setRecs(data.text);
    } finally { setLoadingRecs(false); }
  };

  const kpis = [
    ["Sales", formatIndianCurrency(f.net_sales)],
    ["Quantity", formatIndianGrouping(f.qty_sold)],
    ["Transactions", formatIndianGrouping(f.transactions)],
    ["ATV", formatIndianCurrency(f.atv)],
    ["Margin", formatPct(f.margin)],
    ["YoY", formatPct(f.yoy_change, { signed: true })],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="festival-modal">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-caps">{f.category} festival · {formatDate(f.start_date)} → {formatDate(f.end_date)}</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">{f.name}</h2>
          </div>
          <button onClick={onClose} data-testid="festival-modal-close" className="hairline w-10 h-10 grid place-items-center hover:bg-secondary">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="hairline-b flex gap-0 mb-6 overflow-x-auto">
          {["overview", "products", "history", "recommendations"].map((t) => (
            <button
              key={t}
              data-testid={`festival-tab-${t}`}
              onClick={() => { setTab(t); if (t === "recommendations" && !recs) loadRecs(); }}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-mono whitespace-nowrap ${
                tab === t ? "border-b-2 border-primary text-foreground" : "text-muted-foreground"
              }`}
            >{t}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border mb-8">
              {kpis.map(([k, v]) => (
                <div key={k} className="bg-background p-4">
                  <div className="label-caps">{k}</div>
                  <div className="font-mono text-lg md:text-xl mt-1">{v}</div>
                </div>
              ))}
            </div>
            {f.is_upcoming ? (
              <div className="hairline p-6 bg-secondary/30 text-sm text-muted-foreground" data-testid="festival-upcoming-note">
                This festival window hasn't opened yet. Daily trend will populate once transactions begin on {formatDate(f.start_date)}. In the meantime, check the Recommendations tab for the AI playbook.
              </div>
            ) : (detail?.daily_trend?.length || 0) === 0 ? (
              <div className="hairline p-6 bg-secondary/30 text-sm text-muted-foreground">
                No daily trend data available for this window.
              </div>
            ) : (
              <div className="hairline p-4">
                <div className="label-caps mb-2">Daily trend</div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={detail.daily_trend}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
                    <Tooltip formatter={(v) => formatIndianCurrency(v)} />
                    <Line type="monotone" dataKey="net_sales" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="label-caps mb-3">Top product categories — treemap</div>
              <Treemap items={(detail?.product_breakdown || []).slice(0, 10)} />
              <div className="label-caps mt-3">{detail?.total_categories || 0} categories sold during festival</div>
            </div>
            <div>
              <div className="label-caps mb-3">Top 10 departments</div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={detail?.department_breakdown || []} layout="vertical">
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
                  <YAxis type="category" dataKey="department" stroke="hsl(var(--muted-foreground))" fontSize={10} width={120} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatIndianCurrency(v)} />
                  <Bar dataKey="sales" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="hairline p-6">
            <div className="label-caps mb-3">5-year YoY trend (illustrative)</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[2022, 2023, 2024, 2025, 2026].map((y) => ({ year: y, sales: Math.max(200000, f.net_sales * (0.7 + (y - 2022) * 0.1) * (0.9 + Math.random() * 0.2)) }))}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatIndianCurrency(v)} />
                <Bar dataKey="sales" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {tab === "recommendations" && (
          <div className="hairline p-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-1" />
              <div>
                <div className="font-display text-xl">AI Playbook — {f.name}</div>
                <div className="label-caps mt-1">Claude Sonnet 4.5 · cached 6h</div>
              </div>
            </div>
            <div className="mt-5 whitespace-pre-line text-sm leading-relaxed" data-testid="festival-recommendations-text">
              {loadingRecs ? "Drafting playbook…" : (recs || "Loading recommendations…")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Festivals() {
  const { data } = useSWR("/analysis/festivals", fetcher);
  const [open, setOpen] = useState(null);
  const festivals = data?.festivals || [];

  // Derived
  const upcoming = festivals.filter((f) => f.is_upcoming).sort((a, b) => a.days_away - b.days_away);
  const nextUp = upcoming[0];
  const past = festivals.filter((f) => !f.is_upcoming);
  const totalPastSales = past.reduce((s, f) => s + (f.net_sales || 0), 0);
  const avgYoy = past.length ? past.reduce((s, f) => s + (f.yoy_change || 0), 0) / past.length : 0;
  const biggest = [...past].sort((a, b) => b.net_sales - a.net_sales)[0];

  return (
    <div>
      <PageHeader
        eyebrow="Sales Intelligence · Festivals 2026"
        title="The calendar that runs your floor."
        lede="Twelve to fifteen festivals account for nearly 40% of an Indian fashion year. Here's what each window did and what's coming."
      />

      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps">Festivals tracked</div>
          <div className="font-mono text-2xl mt-2">{festivals.length}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">Past windows · sales</div>
          <div className="font-mono text-2xl mt-2">{formatIndianCurrency(totalPastSales)}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">Avg YoY change</div>
          <div className={`font-mono text-2xl mt-2 ${avgYoy >= 0 ? "text-positive" : "text-destructive"}`}>{formatPct(avgYoy, { signed: true })}</div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps">Biggest window</div>
          <div className="font-display text-lg mt-2 truncate">{biggest?.name || "—"}</div>
          <div className="font-mono text-xs text-muted-foreground mt-1">{biggest ? formatIndianCurrency(biggest.net_sales) : ""}</div>
        </div>
      </div>

      {/* Next upcoming festival callout */}
      {nextUp && (
        <button onClick={() => setOpen(nextUp)} data-testid="next-upcoming-cta"
          className="w-full text-left hairline border-primary/40 bg-primary/5 p-6 md:p-8 mb-10 hover:bg-primary/10 transition-colors">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="label-caps text-primary mb-2">Next upcoming · {nextUp.category}</div>
              <div className="font-display text-3xl md:text-4xl">{nextUp.name}</div>
              <div className="text-sm text-muted-foreground mt-2">{formatDate(nextUp.start_date)} → {formatDate(nextUp.end_date)}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-5xl md:text-6xl text-primary">{nextUp.days_away}<span className="text-2xl ml-2 text-muted-foreground">d</span></div>
              <div className="label-caps mt-1">to opening day</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-4">Click to open the 4-tab playbook with daily trend, product treemap, history and AI recommendations.</div>
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="festivals-grid">
        {festivals.map((f) => <FestivalCard key={f.name} f={f} onOpen={setOpen} />)}
      </div>

      {open && <FestivalModal f={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
